import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import {
    AttendanceStatus, AttendanceStatusUser, InstitutionDashboard, StoreAdminDashboard, UserDashboard,
    AttendanceSummary, LeaveSummary, PeopleSummary, AccountingSummary, InventorySummary, ActionItem, DashboardLayout, WorkSummary,
    AttendanceKpis, DailyCompositionPoint, ArrivalHistogram, WeekdayLatePoint,
    EmployeeLate, EmployeeOt, DepartmentAttendance, TodayLog, SalesSummary, PayrollSummary, ExpenseSummary,
    KpiTarget, KpiTargets,
    ArAgingBucket, TopReceivable, MonthlyIncomeExpense, AccountAmount, StoreValue, WeeklyMovement, ReorderRow, TopItem,
    PipelineStage, MonthlyAmount, QuotationOutcome, TopDeal, PayrollPeriodTotal, DepartmentAmount, MonthlyHours,
    PendingClaimRow, CategoryAmount, TopClaimant, MonthlyLeavePoint, LeaveTypeUsage, HeadcountPoint,
} from '../interfaces/dashboard.interface';
import { ThreadLensData, ThreadLensKey } from '../interfaces/threadLens.interface';

// Every dashboard figure is "as of today", and the server runs in UTC — so the
// caller's zone has to travel with the request or a UTC+6 tenant is shown the
// previous day's numbers between midnight and 06:00 local.
//
// AuthContext also sets axios.defaults.headers.common['X-Timezone'], but that
// cannot be relied on here: it is set in the provider's useEffect, and React runs
// child effects BEFORE parent effects — so the dashboard's own fetch-on-mount
// races ahead of it and goes out bare. Send it explicitly per request, the same
// way attendance.service.ts does.
const tzHeader = () => ({
    ...AuthService.getAuthHeader(),
    'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
});

export const DashboardService = {
    getThreadLens: async (institutionId: string, lens: ThreadLensKey): Promise<ThreadLensData> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.THREAD_LENS(institutionId, lens), {
            headers: tzHeader()
        });
        return response.data;
    },

    getInstitutionDashboard: async (institutionId: string): Promise<InstitutionDashboard> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INSTITUTION(institutionId), {
            headers: tzHeader()
        });
        return response.data;
    },

    getMyDashboard: async (institutionId: string): Promise<UserDashboard> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.MY_SUMMARY(institutionId), {
            headers: tzHeader()
        });
        return response.data;
    },

    getStoreAdminDashboard: async (institutionId: string): Promise<StoreAdminDashboard> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.STORE_ADMIN(institutionId), {
            headers: tzHeader()
        });
        return response.data;
    },

    getAttendanceStatusUsers: async (
        institutionId: string,
        status: AttendanceStatus
    ): Promise<AttendanceStatusUser[]> => {
        const response = await axios.get(
            ENDPOINTS.DASHBOARD.ATTENDANCE_STATUS_USERS(institutionId, status),
            { headers: tzHeader() }
        );
        return response.data;
    },

    getAttendanceSummary: async (institutionId: string): Promise<AttendanceSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ATTENDANCE_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getLeaveSummary: async (institutionId: string): Promise<LeaveSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.LEAVE_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getPeopleSummary: async (institutionId: string): Promise<PeopleSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.PEOPLE_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getAccountingSummary: async (institutionId: string): Promise<AccountingSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getInventorySummary: async (institutionId: string): Promise<InventorySummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getSalesSummary: async (institutionId: string): Promise<SalesSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getPayrollSummary: async (institutionId: string): Promise<PayrollSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.PAYROLL_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getExpenseSummary: async (institutionId: string): Promise<ExpenseSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.EXPENSE_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getWorkSummary: async (institutionId: string): Promise<WorkSummary> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.WORK_SUMMARY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getActionCenter: async (institutionId: string): Promise<ActionItem[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACTION_CENTER(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getAttendanceKpis: async (institutionId: string, days = 30): Promise<AttendanceKpis> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ATTENDANCE_KPIS(institutionId), {
            params: { days }, headers: tzHeader(),
        });
        return response.data;
    },

    getAttendanceComposition: async (institutionId: string, days = 60): Promise<DailyCompositionPoint[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ATTENDANCE_COMPOSITION(institutionId), {
            params: { days }, headers: tzHeader(),
        });
        return response.data;
    },

    getAttendanceArrivals: async (institutionId: string, days = 30): Promise<ArrivalHistogram> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ATTENDANCE_ARRIVALS(institutionId), {
            params: { days }, headers: tzHeader(),
        });
        return response.data;
    },

    getAttendanceWeekday: async (institutionId: string, days = 60): Promise<WeekdayLatePoint[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ATTENDANCE_WEEKDAY(institutionId), {
            params: { days }, headers: tzHeader(),
        });
        return response.data;
    },

    getAttendanceTopLate: async (institutionId: string, days = 30, limit = 8): Promise<EmployeeLate[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ATTENDANCE_TOP_LATE(institutionId), {
            params: { days, limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getAttendanceTopOt: async (institutionId: string, days = 30, limit = 8): Promise<EmployeeOt[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ATTENDANCE_TOP_OT(institutionId), {
            params: { days, limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getAttendanceDepartment: async (institutionId: string, days = 60, orgUnitId?: string): Promise<DepartmentAttendance[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ATTENDANCE_DEPARTMENT(institutionId), {
            params: { days, ...(orgUnitId ? { orgUnitId } : {}) }, headers: tzHeader(),
        });
        return response.data;
    },

    getAttendanceTodayLog: async (institutionId: string): Promise<TodayLog> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ATTENDANCE_TODAY_LOG(institutionId), {
            headers: tzHeader(),
        });
        return response.data;
    },

    getLayout: async (institutionId: string, boardKey = 'home'): Promise<DashboardLayout> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.LAYOUT(institutionId, boardKey), { headers: tzHeader() });
        return response.data;
    },

    saveLayout: async (institutionId: string, boardKey: string, layout: DashboardLayout): Promise<DashboardLayout> => {
        const response = await axios.put(ENDPOINTS.DASHBOARD.LAYOUT(institutionId, boardKey), layout, { headers: tzHeader() });
        return response.data;
    },

    /** Back to what an admin or head set (or the role default); never PUTs the built-in preset. */
    resetMyBoard: async (institutionId: string, boardKey = 'home'): Promise<DashboardLayout> => {
        const response = await axios.post(ENDPOINTS.DASHBOARD_LAYOUTS.MY_BOARD_RESET(institutionId, boardKey), null, { headers: tzHeader() });
        return response.data;
    },

    getKpiTargets: async (institutionId: string): Promise<KpiTargets> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.KPI_TARGETS(institutionId), { headers: tzHeader() });
        return response.data;
    },

    setKpiTargets: async (institutionId: string, targets: KpiTarget[]): Promise<KpiTargets> => {
        const response = await axios.put(ENDPOINTS.DASHBOARD.KPI_TARGETS(institutionId), { targets }, { headers: tzHeader() });
        return response.data;
    },

    getAccountingArAging: async (institutionId: string): Promise<ArAgingBucket[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_AR_AGING(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getAccountingTopReceivables: async (institutionId: string, limit = 8): Promise<TopReceivable[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_TOP_RECEIVABLES(institutionId), {
            params: { limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getAccountingTrend: async (institutionId: string, months = 6): Promise<MonthlyIncomeExpense[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_TREND(institutionId), {
            params: { months }, headers: tzHeader(),
        });
        return response.data;
    },

    getAccountingExpenseByAccount: async (institutionId: string): Promise<AccountAmount[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.ACCOUNTING_EXPENSE_BY_ACCOUNT(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getInventoryValueByStore: async (institutionId: string): Promise<StoreValue[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_VALUE_BY_STORE(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getInventoryMovement: async (institutionId: string, weeks = 8): Promise<WeeklyMovement[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_MOVEMENT(institutionId), {
            params: { weeks }, headers: tzHeader(),
        });
        return response.data;
    },

    getInventoryReorder: async (institutionId: string, limit = 10): Promise<ReorderRow[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_REORDER(institutionId), {
            params: { limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getInventoryTopItems: async (institutionId: string, limit = 8): Promise<TopItem[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.INVENTORY_TOP_ITEMS(institutionId), {
            params: { limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getSalesPipeline: async (institutionId: string): Promise<PipelineStage[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_PIPELINE(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getSalesRevenue: async (institutionId: string, months = 6): Promise<MonthlyAmount[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_REVENUE(institutionId), {
            params: { months }, headers: tzHeader(),
        });
        return response.data;
    },

    getSalesOutcomes: async (institutionId: string, days = 90): Promise<QuotationOutcome[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_OUTCOMES(institutionId), {
            params: { days }, headers: tzHeader(),
        });
        return response.data;
    },

    getSalesTopDeals: async (institutionId: string, limit = 8): Promise<TopDeal[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.SALES_TOP_DEALS(institutionId), {
            params: { limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getPayrollTrend: async (institutionId: string, periods = 6): Promise<PayrollPeriodTotal[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.PAYROLL_TREND(institutionId), {
            params: { periods }, headers: tzHeader(),
        });
        return response.data;
    },

    getPayrollByDepartment: async (institutionId: string): Promise<DepartmentAmount[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.PAYROLL_BY_DEPARTMENT(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getPayrollOvertime: async (institutionId: string, months = 6): Promise<MonthlyHours[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.PAYROLL_OVERTIME(institutionId), {
            params: { months }, headers: tzHeader(),
        });
        return response.data;
    },

    getExpenseTrend: async (institutionId: string, months = 6): Promise<MonthlyAmount[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.EXPENSE_TREND(institutionId), {
            params: { months }, headers: tzHeader(),
        });
        return response.data;
    },

    getExpenseByCategory: async (institutionId: string): Promise<CategoryAmount[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.EXPENSE_BY_CATEGORY(institutionId), { headers: tzHeader() });
        return response.data;
    },

    getExpenseQueue: async (institutionId: string, limit = 10): Promise<PendingClaimRow[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.EXPENSE_QUEUE(institutionId), {
            params: { limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getExpenseTopClaimants: async (institutionId: string, days = 90, limit = 8): Promise<TopClaimant[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.EXPENSE_TOP_CLAIMANTS(institutionId), {
            params: { days, limit }, headers: tzHeader(),
        });
        return response.data;
    },

    getLeaveTrend: async (institutionId: string, months = 6): Promise<MonthlyLeavePoint[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.LEAVE_TREND(institutionId), {
            params: { months }, headers: tzHeader(),
        });
        return response.data;
    },

    getLeaveByType: async (institutionId: string): Promise<LeaveTypeUsage[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.LEAVE_BY_TYPE(institutionId), {
            headers: tzHeader(),
        });
        return response.data;
    },

    getPeopleGrowth: async (institutionId: string, months = 12): Promise<HeadcountPoint[]> => {
        const response = await axios.get(ENDPOINTS.DASHBOARD.PEOPLE_GROWTH(institutionId), {
            params: { months }, headers: tzHeader(),
        });
        return response.data;
    },
};
